import { useState, useEffect } from 'react';
import { StreamStatus } from '../services/apiService';

// Status to step mapping for streaming
const statusToStepMap: Record<StreamStatus, { text: string; stepIndex: number }> = {
  'thinking': { text: 'Thinking', stepIndex: 0 },
  'scoping': { text: 'Scoping query', stepIndex: 1 },
  'broadening': { text: 'Broadening search', stepIndex: 2 },
  'applying_filters': { text: 'Applying filters', stepIndex: 3 },
  'scoring': { text: 'Almost there', stepIndex: 4 },
  'compiling': { text: 'Compiling summary & results', stepIndex: 5 },
  'complete': { text: 'Complete', stepIndex: 6 },
  'error': { text: 'Error', stepIndex: -1 },
  'followup': { text: 'Processing', stepIndex: 2 },
  'out_of_scope': { text: 'Processing', stepIndex: 2 },
  'no_results': { text: 'Searching', stepIndex: 3 },
};

const streamingSteps = [
  'Thinking',
  'Scoping query',
  'Broadening search',
  'Applying filters',
  'Almost there',
  'Compiling summary & results',
  'Complete'
];

const regularThinkingSteps = [
  { text: 'Scoping query', minTime: 2000, maxTime: 4000 },
  { text: 'Applying filters', minTime: 2000, maxTime: 3000 },
  { text: 'Broadening search', minTime: 2000, maxTime: 4000 },
  { text: 'Fetching sources', minTime: 2000, maxTime: 4000 },
  { text: 'Compiling results', minTime: Infinity, maxTime: Infinity } // Stays until end
];

const extensiveThinkingSteps = [
  { text: 'Scoping query', minTime: 2000, maxTime: 4000 },
  { text: 'Applying filters', minTime: 2000, maxTime: 3000 },
  { text: 'Broadening search', minTime: 2000, maxTime: 4000 },
  { text: 'Taking longer than expected', minTime: 3000, maxTime: 6000 },
  { text: 'Almost there', minTime: 2000, maxTime: 4000 },
  { text: 'Compiling results', minTime: Infinity, maxTime: Infinity } // Stays until end
];

interface ThinkingAnimationProps {
  className?: string;
  delay?: number; // Delay in ms before starting progression (default 1000ms)
  isExtensiveSearch?: boolean; // Whether this is an extensive search
  // Streaming props
  streamingStatus?: StreamStatus | null; // Current status from SSE stream
  streamingMessage?: string | null; // Custom message from stream
  resultsCount?: number; // Number of results found so far
}

export const ThinkingAnimation: React.FC<ThinkingAnimationProps> = ({ 
  className = "text-grayscale-1100 font-grotesk text-sm",
  delay = 1000,
  isExtensiveSearch = false,
  streamingStatus = null,
  streamingMessage = null,
  resultsCount = 0
}) => {
  // Check if we're in streaming mode
  const isStreaming = streamingStatus !== null;
  
  // Select the appropriate thinking steps based on search type (for non-streaming)
  const thinkingSteps = isExtensiveSearch ? extensiveThinkingSteps : regularThinkingSteps;
  
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [showProgression, setShowProgression] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [dots, setDots] = useState('');
  const [prevResultsCount, setPrevResultsCount] = useState(0);
  const [countAnimation, setCountAnimation] = useState<'up' | 'down' | null>(null);
  const [displayedResultsCount, setDisplayedResultsCount] = useState(0); // Gradually animated count

  // Gradually animate results count when it changes
  useEffect(() => {
    if (resultsCount !== displayedResultsCount) {
      const direction = resultsCount > displayedResultsCount ? 'up' : 'down';
      setCountAnimation(direction);
      
      // Animate count gradually
      const targetCount = resultsCount;
      const startCount = displayedResultsCount;
      const difference = Math.abs(targetCount - startCount);
      
      if (difference === 0) {
        setCountAnimation(null);
        return;
      }
      
      // Calculate animation duration (faster for small changes, slower for large)
      const duration = Math.min(difference * 80, 800); // Max 800ms
      const stepDuration = Math.max(50, duration / difference); // Min 50ms per step
      
      let currentValue = startCount;
      const increment = direction === 'up' ? 1 : -1;
      
      const interval = setInterval(() => {
        currentValue += increment;
        
        // Check if we've reached or passed the target
        if ((direction === 'up' && currentValue >= targetCount) || 
            (direction === 'down' && currentValue <= targetCount)) {
          setDisplayedResultsCount(targetCount);
          setCountAnimation(null);
          clearInterval(interval);
        } else {
          setDisplayedResultsCount(currentValue);
        }
      }, stepDuration);
      
      return () => clearInterval(interval);
    }
  }, [resultsCount, displayedResultsCount]);

  // Track results count changes for visual feedback
  useEffect(() => {
    if (resultsCount !== prevResultsCount) {
      setPrevResultsCount(resultsCount);
    }
  }, [resultsCount, prevResultsCount]);

  // Reset step index when search type changes
  useEffect(() => {
    setCurrentStepIndex(0);
    setShowProgression(false);
  }, [isExtensiveSearch]);

  // Reset displayed count when streaming starts - always start at 0
  useEffect(() => {
    if (isStreaming) {
      setDisplayedResultsCount(0);
    }
  }, [isStreaming]);

  // Handle streaming status changes - ensure step index only moves forward
  useEffect(() => {
    if (isStreaming && streamingStatus) {
      const stepInfo = statusToStepMap[streamingStatus];
      if (stepInfo && stepInfo.stepIndex >= 0) {
        setCurrentStepIndex(prev => {
          // Only allow forward progression (or staying at same level)
          // Never go backwards unless it's a reset (error or complete)
          if (streamingStatus === 'error' || streamingStatus === 'complete') {
            return stepInfo.stepIndex; // Allow reset for error/complete
          }
          // For normal progression, only move forward
          // Once we've moved past thinking (index 0), never go back to it
          if (prev > 0 && stepInfo.stepIndex === 0) {
            return prev; // Don't go back to thinking once we've progressed
          }
          if (stepInfo.stepIndex > prev) {
            return stepInfo.stepIndex;
          }
          // If same or less, keep current (unless it's thinking at the start)
          if (stepInfo.stepIndex === 0 && prev === 0) {
            return 0; // Allow thinking at start
          }
          return prev; // Keep current step if trying to go backwards
        });
        setShowProgression(true);
      }
    }
  }, [isStreaming, streamingStatus]);

  useEffect(() => {
    // Skip auto-progression if streaming
    if (isStreaming) return;
    
    // Wait for delay before starting progression
    const delayTimer = setTimeout(() => {
      setShowProgression(true);
    }, delay);

    return () => clearTimeout(delayTimer);
  }, [delay, isStreaming]);

  useEffect(() => {
    // Skip auto-progression if streaming
    if (isStreaming) return;
    if (!showProgression) return;
    
    // Don't progress beyond the last step (Compiling results)
    if (currentStepIndex >= thinkingSteps.length - 1) return;

    const currentStep = thinkingSteps[currentStepIndex];
    
    // Calculate random time for this specific step
    const randomTime = Math.random() * (currentStep.maxTime - currentStep.minTime) + currentStep.minTime;
    
    const progressToNext = () => {
      // Fade out
      setIsTransitioning(true);
      
      // Change to next step during fade
      setTimeout(() => {
        setCurrentStepIndex((prev) => Math.min(prev + 1, thinkingSteps.length - 1));
        setIsTransitioning(false);
      }, 300);
    };
    
    const timeoutId = setTimeout(progressToNext, randomTime);

    return () => clearTimeout(timeoutId);
  }, [showProgression, currentStepIndex, thinkingSteps, isStreaming]);

  // Animated dots effect
  useEffect(() => {
    const dotsInterval = setInterval(() => {
      setDots(prev => {
        if (prev === '...') return '';
        return prev + '.';
      });
    }, 450);

    return () => clearInterval(dotsInterval);
  }, []);

  // Determine current text based on streaming vs non-streaming mode
  // In streaming mode, show the status progression: Thinking -> Scoping query -> Broadening search -> Applying filters -> Almost there -> Compiling summary & results
  let currentText: string;
  if (isStreaming && streamingStatus) {
    // Use the mapped text from statusToStepMap for the current status
    const stepInfo = statusToStepMap[streamingStatus];
    currentText = stepInfo?.text || 'Thinking';
  } else {
    currentText = showProgression 
      ? thinkingSteps[currentStepIndex].text 
      : 'Thinking';
  }

  // Determine which steps to show for progress indicator
  const progressSteps = isStreaming ? streamingSteps : thinkingSteps;
  // Use currentStepIndex which is properly managed to prevent backward jumps
  const activeStepIndex = currentStepIndex;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
        {/* Pulsing indicator */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <div className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary-500"></span>
          </div>
        </div>
        
        {/* Text with smooth transition */}
        <div className="flex items-center gap-1 min-w-0 flex-1 sm:flex-initial">
          <p 
            className={`${className} transition-all duration-300 ${
              isTransitioning ? 'opacity-0 -translate-x-2 scale-95' : 'opacity-100 translate-x-0 scale-100'
            }`}
            style={{ transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)' }}
          >
            {currentText}
          </p>
          <span className={`${className} inline-block w-6 font-medium flex-shrink-0`}>
            {dots}
          </span>
        </div>
        
        {/* Results counter with animation - always show in streaming mode, starting at 0 */}
        {isStreaming && (
          <div className={`flex items-center gap-0.5 sm:gap-1 px-1.5 sm:px-2 py-0.5 rounded-full bg-primary-100 transition-all duration-300 flex-shrink-0 ${
            countAnimation === 'up' ? 'scale-110 bg-green-100' : 
            countAnimation === 'down' ? 'scale-95 bg-orange-100' : ''
          }`}>
            <span className={`text-xs font-semibold transition-colors duration-300 ${
              countAnimation === 'up' ? 'text-green-600' : 
              countAnimation === 'down' ? 'text-orange-600' : 'text-primary-600'
            }`}>
              {displayedResultsCount}
            </span>
            <span className="text-xs text-primary-500 hidden min-[400px]:inline">
              {displayedResultsCount === 1 ? 'result' : 'results'}
            </span>
            <span className="text-xs text-primary-400 hidden sm:inline">found</span>
            {countAnimation === 'down' && (
              <span className="text-xs text-orange-500 animate-pulse hidden sm:inline">↓</span>
            )}
            {countAnimation === 'up' && (
              <span className="text-xs text-green-500 animate-pulse hidden sm:inline">↑</span>
            )}
          </div>
        )}
      </div>
      
      {/* Progress indicator */}
      {showProgression && (
        <div className="flex items-center gap-1 ml-5">
          {progressSteps.slice(0, -1).map((step, index) => (
            <div key={index} className="flex items-center">
              <div
                className={`h-1.5 rounded-full transition-all duration-500 ${
                  index < activeStepIndex 
                    ? 'w-6 bg-primary-500' 
                    : index === activeStepIndex 
                    ? 'w-8 bg-primary-400 animate-pulse' 
                    : 'w-4 bg-gray-200'
                }`}
              />
              {index < progressSteps.length - 2 && (
                <div className={`w-1 h-0.5 transition-colors duration-500 ${
                  index < activeStepIndex ? 'bg-primary-400' : 'bg-gray-200'
                }`} />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

